import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

class IntegrationService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/integrations`,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const projectId = localStorage.getItem('currentProjectId');
      if (projectId) config.headers['X-Project-Id'] = projectId;
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/signin';
        }
        return Promise.reject(error);
      }
    );
  }

  // Get all integrations for the user
  async getIntegrations() {
    try {
      const response = await this.api.get('/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get integration by type
  async getIntegrationByType(type) {
    try {
      const response = await this.api.get(`/${type}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { success: false, data: null };
      }
      throw this.handleError(error);
    }
  }

  // Create or update integration
  async createOrUpdateIntegration(integrationData) {
    try {
      const response = await this.api.post('/', integrationData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Test integration connection
  async testIntegration(type, config) {
    try {
      const response = await this.api.post('/test', { type, config });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Send test email
  async sendTestEmail(config) {
    try {
      const response = await this.api.post('/test-email', { config });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Activate integration
  async activateIntegration(type) {
    try {
      const response = await this.api.put(`/${type}/activate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Deactivate integration
  async deactivateIntegration(type) {
    try {
      const response = await this.api.put(`/${type}/deactivate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete integration
  async deleteIntegration(type) {
    try {
      const response = await this.api.delete(`/${type}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get integration statistics
  async getIntegrationStats(type) {
    try {
      const response = await this.api.get(`/${type}/stats`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle API errors
  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    return new Error(error.message || 'An unexpected error occurred');
  }
}

export default new IntegrationService();
